import React, { useEffect, useState, useCallback } from "react";
import config from "../../config";
import ModelRankList from "../../components/ModelRankList";

const base = config.api.baseUrl;
const E = config.api.endpoints.claudeAgent;
const modelUrl = (id) => base + E.adminModel.replace(":id", encodeURIComponent(id));

async function call(url, opts = {}) {
  const r = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
  return j.data;
}

const EMPTY = { id: "", label: "", input_per_mtok: "", output_per_mtok: "" };

/**
 * Admin editor for the Claude Agent model catalog: drag to rank, toggle
 * active, add, remove. Every mutation re-fetches the list because the server
 * decides which row is the default (first active by rank).
 */
export default function ModelCatalogPanel() {
  const [models, setModels] = useState([]);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setModels(await call(base + E.adminModels));
      setError(null);
    } catch (e) {
      setError(`Failed to load model catalog: ${e.message}`);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const mutate = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onToggle = (m) => mutate(() =>
    call(modelUrl(m.id), { method: "PATCH", body: JSON.stringify({ active: !m.active }) }));

  const onRemove = (m) => {
    if (!window.confirm(`Remove ${m.label} (${m.id}) from the catalog? Existing conversations keep working.`)) return;
    mutate(() => call(modelUrl(m.id), { method: "DELETE" }));
  };

  const onAdd = (e) => {
    e.preventDefault();
    mutate(async () => {
      await call(base + E.adminModels, {
        method: "POST",
        body: JSON.stringify({
          id: draft.id.trim(),
          label: draft.label.trim(),
          input_per_mtok: Number(draft.input_per_mtok),
          output_per_mtok: Number(draft.output_per_mtok),
        }),
      });
      setDraft(EMPTY);
    });
  };

  const onReorder = async (reordered) => {
    const before = models;
    setModels(reordered); // synchronous, so the dropped row doesn't snap back
    try {
      setModels(await call(base + E.adminModelOrder, {
        method: "PUT",
        body: JSON.stringify({ ordered_ids: reordered.map((m) => m.id) }),
      }));
      setError(null);
    } catch (e) {
      setModels(before);
      setError(`Failed to save model order: ${e.message}`);
    }
  };

  const setField = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div className="ca-admin-models" style={{ marginBottom: 16 }}>
      <h3>Models</h3>
      <p style={{ opacity: 0.6, fontSize: 13, margin: "4px 0 8px" }}>
        Drag to reorder. The first active model is the default for new conversations,
        and the picker lists active models in this order. Inactive models are hidden
        from users but stay available to admin-defined agents. Saved automatically.
      </p>
      {error && <div className="ca-admin-error">{error}</div>}
      {models.length === 0 ? (
        <p style={{ opacity: 0.6, fontSize: 13 }}>Model catalog unavailable.</p>
      ) : (
        <ModelRankList
          items={models}
          getKey={(m) => m.id}
          topBadge={null}
          renderItem={(m) => (
            <span className={`ca-model-row${m.active ? "" : " inactive"}`}>
              <input
                type="checkbox"
                aria-label={`${m.label} active`}
                checked={m.active}
                disabled={busy}
                onChange={() => onToggle(m)}
              />
              <strong>{m.label}</strong>
              <code>{m.id}</code>
              {m.is_default ? (
                <span className="model-rank-default-badge">default</span>
              ) : m.active && (
                <button type="button" disabled={busy} title="Move to the top: new chats and agents without a model use it"
                        onClick={() => onReorder([m, ...models.filter((x) => x.id !== m.id)])}>
                  Make default
                </button>
              )}
              <span className="ca-model-price">
                ${m.input_per_mtok} / ${m.output_per_mtok} per MTok
              </span>
              <button type="button" aria-label={`Remove ${m.label}`} disabled={busy}
                      onClick={() => onRemove(m)}>
                Remove
              </button>
            </span>
          )}
          onReorder={onReorder}
        />
      )}
      <form className="ca-form ca-model-add" onSubmit={onAdd}>
        <label>Model id
          <input value={draft.id} onChange={setField("id")} placeholder="claude-opus-5" required />
        </label>
        <label>Label
          <input value={draft.label} onChange={setField("label")} placeholder="Opus 5" required />
        </label>
        <label>Input $/MTok
          <input type="number" min="0" step="0.01" value={draft.input_per_mtok}
                 onChange={setField("input_per_mtok")} required />
        </label>
        <label>Output $/MTok
          <input type="number" min="0" step="0.01" value={draft.output_per_mtok}
                 onChange={setField("output_per_mtok")} required />
        </label>
        <button type="submit" disabled={busy}>Add model</button>
      </form>
    </div>
  );
}
